import React, { 
  useState, 
  useEffect, 
  useRef, 
  useCallback, 
  forwardRef, 
  useImperativeHandle 
} from 'react'

import './UnifiedTextBox.css'

// Utilities
import { getChordValidationError } from '../utils/intentDetection'

/**
 * UnifiedTextBox component
 * A sophisticated text input that handles chord progression parsing
 * and provides real-time validation feedback
 */

// Define the interface for the ref methods exposed to parent
export interface UnifiedTextBoxRef {
  clearInput: (shouldRefocus?: boolean) => void;
}

// Define props interface
interface UnifiedTextBoxProps {
  onGenerate: (value: string) => void;
  onShowPopup: (value: string) => void;
  onHidePopup: () => void;
  value?: string;
  onInputChange?: (value: string) => void;
  onParsingError?: (error: string | null) => void;
  placeholder?: string;
  className?: string;
}

const UnifiedTextBox = forwardRef<UnifiedTextBoxRef, UnifiedTextBoxProps>(({ 
  onGenerate, 
  onShowPopup, 
  onHidePopup,
  value = "",
  onInputChange,
  onParsingError,
  placeholder = "Type chords or search presets...",
  className = ""
}, ref) => {
  // Use only controlled state through value prop - no internal input state
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Expose clearInput function to parent through ref
  useImperativeHandle(ref, () => ({
    clearInput: (shouldRefocus: boolean = true) => {
      if (onInputChange) {
        onInputChange('');
      }
      setParsingError(null);
      if (onParsingError) {
        onParsingError(null);
      }
      onHidePopup();
      // Only refocus if explicitly requested
      if (shouldRefocus) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    }
  }));

  // Debounced validation with error checking
  const analyzeInput = useCallback((_inputValue: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      // Always use the current value from the input ref to avoid stale closures
      const currentValue = inputRef.current?.value || '';
      
      // Check for parsing errors if input exists
      const error = getChordValidationError(currentValue);
      setParsingError(error);
      
      // Pass error state to parent
      if (onParsingError) {
        onParsingError(error);
      }
      
      // Show popup if there's meaningful content
      if (currentValue.trim().length > 0) {
        onShowPopup(currentValue);
      } else {
        onHidePopup();
      }
    }, 200); // Reduced to 200ms for more responsive feel
  }, [onShowPopup, onHidePopup, onParsingError]);

  // Analyze input whenever value changes
  useEffect(() => {
    if (value.trim()) {
      analyzeInput(value);
    } else {
      setParsingError(null); // Clear error when input is empty
      // Clear error state in parent too
      if (onParsingError) {
        onParsingError(null);
      }
    }
  }, [value, analyzeInput, onParsingError]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Call external onChange handler - this updates the parent's state
    if (onInputChange) {
      onInputChange(newValue);
    }
  };

  // Handle key presses
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      // Clear the input and hide popup
      if (onInputChange) {
        onInputChange('');
      }
      setParsingError(null);
      if (onParsingError) {
        onParsingError(null);
      }
      onHidePopup();
      inputRef.current?.blur();
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    if (value.trim() === '') return;

    // Always generate when there's input - user can decide what they want
    onGenerate(value);
  };

  // Handle focus events
  const handleFocus = () => {
    setIsFocused(true);
    // Always show popup on focus if there's content
    if (value.trim() !== '') {
      analyzeInput(value);
    } else {
      // Show popup with empty state to display all presets and library
      onShowPopup('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    
    // Don't auto-hide popup on blur if user is actively interacting with it
    // The popup should handle its own closing logic
    // This prevents accidental closes when clicking on non-focusable elements in the popup
  };

  // Get appropriate placeholder based on state
  const getPlaceholder = (): string => {
    if (isFocused) {
      return "Try: C Am F G  or  jazz progressions";
    }
    return placeholder;
  };

  // Clear input function for internal use
  const clearInput = () => {
    if (onInputChange) {
      onInputChange('');
    }
    setParsingError(null);
    if (onParsingError) {
      onParsingError(null);
    }
    onHidePopup();
    // Don't refocus input after clearing to prevent popup from reappearing
  };

  return (
    <div className={`unified-text-box ${className}`}>
      <div className="input-container">
        <span className="input-icon" aria-hidden="true">
          🔍
        </span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={getPlaceholder()}
          className="unified-input"
          aria-label="Enter chord progression or search presets"
          aria-describedby="input-help"
          aria-invalid={parsingError ? 'true' : 'false'}
        />
        {/* Quick Create button - only visible when focused and has input */}
        {isFocused && value.trim() && !parsingError && (
          <button
            type="button"
            className="quick-create-button"
            onClick={handleSubmit}
            onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking button
            aria-label="Generate guitars from current input"
          >
            + Create
          </button>
        )}
        {value && (
          <button
            type="button"
            className="clear-button"
            onClick={clearInput}
            aria-label="Clear input"
          >
            ✕
          </button>
        )}
      </div>
      <div id="input-help" className="sr-only">
        Type chord names like C Am F G, or search terms like jazz
      </div>
    </div>
  );
});

// Add display name for debugging
UnifiedTextBox.displayName = 'UnifiedTextBox';

export default UnifiedTextBox;
